import moment from "moment";
import { mapState } from "vuex";
import { countByKeys } from "@/utils/countByKeys";
import { percentFromNumber } from "@/utils/percentFromNumber";
import { MemberInviteStatuses } from "@/enums/MemberInviteStatuses";
import { FamilyHubAppStatus } from "@/enums/FamilyHubAppStatus";
import Loader from "@/components/loader/Loader.vue";
import { PremiumServiceEventTypeEnum, PremiumServiceEventTypes, PremiumServiceProductTypeEnum, PremiumServiceRequestTypeEnum } from "@/enums/PremiumServiceEventTypes";
import { calculateServicesSpentMoney } from "@/utils/calculateServicesSpentMoney";
import { formatCurrency } from "@/utils/formatCurrency";

export default {
  name: 'ReportFamily',
  components: { Loader },
  data() {
    return {
      loading: true,
      loadingLikes: true,
      eventTypes: Object.values(PremiumServiceEventTypeEnum),
      requestTypes: Object.values(PremiumServiceRequestTypeEnum),
      productTypes: Object.values(PremiumServiceProductTypeEnum),
      statusColor: {
        [MemberInviteStatuses.CreatedWithoutInvitation]: '#F44336',
        [MemberInviteStatuses.InvitationAccepted]: '#4CAF50',
        [MemberInviteStatuses.InvitationSent]: '#FF9700'
      },
      defaultHeader: [
        {
          text: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Name"],
          value: "name",
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]["ReportFamily.BookingsAndOrders"],
          value: "servicesCount",
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Type"],
          value: "serviceItemType",
        }
      ],
      servicesHeaders: [
        { text: this.$t('GeneralTranslations.Name'), align: "start", value: "name", width: 180 },
        { text: this.$t('GeneralTranslations.Type'), value: "serviceItemType", width: 180 },
        { text: this.$t('GeneralTranslations.Resident'), value: "residentName", width: 180 },
        { text: this.$t('ReportFamily.BookingsAndOrders'), value: "bookedCount", width: 180 },
        { text: this.$t('ReportFamily.TotalSpent'), value: "spentMoney", width: 180 },
        { text: this.$t('ReportFamily.SpentAverage'), value: "spentAverage", width: 180 },
        { text: this.$t('GeneralTranslations.Provider'), value: "providerName", width: 180 },
        { text: this.$t('GeneralTranslations.Dimension'), value: "category", width: 180 },
      ],
      services: [],
      date: {
        start: moment().subtract(1, "week").format("YYYY-MM-DD"),
        end: moment().format("YYYY-MM-DD")
      },
      picker: {
        start: false,
        end: false
      },
      period: "week",
      periods: [
        { title: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Week"], value: "week" },
        { title: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Month"], value: "month" },
        { title: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Year"], value: "year" }
      ],
    }
  },
  async mounted() {
    await this.loadReportData()
  },
  computed: {
    ...mapState({
      familyMemberInvitations: state => (state as any).residentModule.familyMemberInvitations,
      facility: (state) => (state as any).facilityModule.selectedFacility,
      additionalServices: (state) => (state as any).additionalServicesModule.additionalServices,
    }),

    conciergeRequestsCount() {
      return this.services
        .filter(r => r.serviceItemType === PremiumServiceEventTypes.Request)
        .reduce((acc, r) => acc + (r.bookedCount || 0), 0);
    },

    validServiceIds() {
      return new Set(this.additionalServices.map(s => s.id));
    },

    validResidentIds() {
      return new Set(this.residents.map(r => r.id));
    },

    familyServiceRequestsWithIds() {
      const vs = this.validServiceIds;
      const vr = this.validResidentIds;
      return this.familyServiceRequests.filter(req => {
        const sid = req.serviceId || req.service?.id;
        const rid = req.residentId || req.resident?.id;
        return !!sid && !!rid && vs.has(sid) && vr.has(rid);
      });
    },

    familyMembers() {
      return this.$store.state.residentModule.familyMembers;
    },

    familyMembersAppActivated() {
      return this.$store.state.residentModule.familyMembers.filter(x => x.appStatus === FamilyHubAppStatus.Activated);
    },

    residents() {
      return this.$store.state.residentModule.residentsShortData
    },

    familyLikes(): any[] {
      return this.$store.state.likesModule.familyLikes;
    },

    familyServiceRequests() {
      const start = moment(this.date.start).startOf('day').toDate();
      const end   = moment(this.date.end).add(1, 'day').startOf('day').toDate();

      return this.$store.state.additionalServicesModule.serviceRequests
        .filter(x => x.bookByFamilyId?.length > 0)
        .filter(x => {
          const d = x?.createdDateTime?.toDate ? x.createdDateTime.toDate() : new Date(x.createdDateTime);
          return d >= start && d < end;
        });
    },
   
    spentMoney() {
      const spentMoney = this.calculateSpentMoney(this.familyServiceRequestsWithIds)
      return spentMoney;
    },

    facilityCurrency() {
      return this.$store.getters.facilityCurrency;
    },

    servicesPopularity() {
      const serviceCountMap = {};
      this.familyServiceRequestsWithIds.forEach(service => {
        if (serviceCountMap[service.serviceName]) {
          serviceCountMap[service.serviceName].servicesCount++;
        } else {
          serviceCountMap[service.serviceName] = {
            name: service.serviceName,
            serviceItemType: service?.serviceItemType || '',
            servicesCount: 1
          };
        }
      });
      const groupedServices = Object.values(serviceCountMap)
      const mostPopularServices = [...groupedServices].sort((a: any, b: any) => b.servicesCount - a.servicesCount)
      const leastPopularServices = [...groupedServices].sort((a: any, b: any) => a.servicesCount - b.servicesCount)

      return {
        mostPopularServices: mostPopularServices,
        leastPopularServices: leastPopularServices
      }
    },

    formattedSpentMoney() {
      return formatCurrency(this.spentMoney, this.facilityCurrency);
    },

    mostPopularServices() {
      return this.servicesPopularity.mostPopularServices;
    },

    conciergeEvents() {
      return this.familyServiceRequestsWithIds.filter((x) => this.resolveRequestBucket(x) === PremiumServiceEventTypes.Event)
    },

    conciergeRequests() {
      return this.familyServiceRequestsWithIds.filter((x) => this.resolveRequestBucket(x) === PremiumServiceEventTypes.Request)
    },

    conciergeProducts() {
      return this.familyServiceRequestsWithIds.filter((x) => this.resolveRequestBucket(x) === PremiumServiceEventTypes.Product)
    },

    residentsWithFamilyActiveOnApp() {
      const familyMembersAppActivate = this.familyMembersAppActivated;

      if (!familyMembersAppActivate) return []

      const allResidents = this.residents

      const activatedResidentsIds = familyMembersAppActivate.reduce((acc, familyMember) => {
        const residentIdsInShortData = familyMember.residentIds.filter(residentId => allResidents.some(resident => resident.id === residentId));

        acc.push(...residentIdsInShortData);
        return acc;
      }, []);

      const residentsWithFamily = allResidents.filter(resident => activatedResidentsIds.includes(resident.id));

      return residentsWithFamily;
    },

    memberInviteStatuses() {
      return MemberInviteStatuses
    },

    percentOfAppActivatedFamilyMembers() {
      const percent = percentFromNumber(
        this.familyMembers.length,
        this.familyMembersAppActivated.length
      );

      return isNaN(percent) ? 0 : percent.toFixed(0);
    },

    percentOfResidentsWithFamilyActiveOnApp() {
      const percent = percentFromNumber(
        this.residents.length,
        this.residentsWithFamilyActiveOnApp.length
      );

      return isNaN(percent) ? 0 : percent.toFixed(0);
    },

    servicesByStatus() {
      return countByKeys(this.familyMemberInvitations, "status");
    },

    invitationsByStatus() {
      return this.familyMemberInvitations.reduce((result, current) => {

        const status = current["status"];
        const date = moment(current.invitationDateTime).format("DD/MM");
        const findedDate = result.find(invite => invite.name === status);

        if (findedDate) {
          findedDate.data[date] = findedDate.data[date] ? findedDate.data[date] + 1 : 1;
        } else {
          result.push({ name: status, data: { [date]: 1 }, color: this.statusColor[status] });
        }

        return result;
      }, []);
    },

    formattedSelectedDateRange() {
      return `${moment(this.date.start).format(this.facility.dateFormat)} - ${moment(this.date.end).format(this.facility.dateFormat)}`;
    },
    formattedStartDate() {
      return moment(this.date.start).format(this.facility.dateFormat)
    },
    formattedEndDate() {
      return moment(this.date.end).format(this.facility.dateFormat)
    },
  },
  methods: {
    getRequestCost(req) {
      const toNum = (v) => {
        if (v == null) return 0;
        const n = parseFloat(String(v).replace(/[^\d.-]/g, ''));
        return Number.isFinite(n) ? n : 0;
      };

      if (Array.isArray(req.items)) return toNum(this.calculateSpentMoney([req]));
      return (
        toNum(req.paymentCost) || 0
      );
    },
    resolveRequestBucket(req) {
      const sid = (req && (req.serviceId || (req.service && req.service.id))) || null;
      if (!sid) return null;

      const svc = this.$store.state.additionalServicesModule.additionalServices.find(s => s.id === sid);
      if (!svc) return null;

      if (this.eventTypes.includes(svc.type))   return PremiumServiceEventTypes.Event;
      if (this.requestTypes.includes(svc.type)) return PremiumServiceEventTypes.Request;
      if (this.productTypes.includes(svc.type)) return PremiumServiceEventTypes.Product;
      return null;
    },
    calculateSpentMoney(serviceRequests) {
      return calculateServicesSpentMoney(serviceRequests);
    },
    calculateFontSize() {
      const textLength = this.spentMoney.toString().length;
      const baseFontSize = 4.2;
      const d = textLength > 10 ? 4 : 3.3
      const calculatedFontSize = baseFontSize - (textLength / d);
      return `${calculatedFontSize}vw`;
    },
    calculateServicePercentByType(name) {
      const percent = percentFromNumber(
        this.familyMemberInvitations.length,
        this.servicesByStatus[name]
      );

      return isNaN(percent) ? 0 : percent.toFixed(2);
    },
    async loadReportData() {
      this.loading = true;
      this.loadingLikes = true;

      const startM = moment(this.date.start).startOf('day');
      const endM = moment(this.date.end).add(1, 'day').startOf('day');
      const startInclusive = startM.toDate();
      const endExclusive = endM.toDate();
      const isYearOrMore = endM.diff(startM, 'years', true) >= 1;

      const basePromises = [
        this.$store.dispatch("getAdditionalServices"),
        this.$store.dispatch("getServiceRequests",   { start: startInclusive, end: endExclusive }),
        this.$store.dispatch("getFamilyMembers",     { start: startInclusive, end: endExclusive }),
      ];

      const likesPromise = this.$store.dispatch("getFamilyLikes", { fromDate: startInclusive, toDate: endExclusive });

      try {
        if (isYearOrMore) {
          await Promise.all(basePromises);
          this.services = this.mapServices();
          this.loading = false;
          await likesPromise;
        } else {
          await Promise.all([...basePromises, likesPromise]);
          this.services = this.mapServices();
          this.loading = false;
        }
      } finally {
        this.loadingLikes = false;
      }
    },
    
   mapServices() {
      const serviceMetaById = new Map();
      this.$store.state.additionalServicesModule.additionalServices.forEach((s) => {
        const meta = { ...s };
        if (this.eventTypes.includes(s.type))       meta.serviceItemType = PremiumServiceEventTypes.Event;
        else if (this.requestTypes.includes(s.type)) meta.serviceItemType = PremiumServiceEventTypes.Request;
        else if (this.productTypes.includes(s.type)) meta.serviceItemType = PremiumServiceEventTypes.Product;
        serviceMetaById.set(s.id, meta);
      });

      const byServiceResident = new Map();
      const seen = new Set();

      this.familyServiceRequestsWithIds.forEach((req) => {
        const requestId = req.id || req.requestId;
        if (requestId && seen.has(requestId)) return;
        if (requestId) seen.add(requestId);

        const bucket = this.resolveRequestBucket(req);
        if (!bucket) return;

        const serviceId  = req.serviceId || req.service?.id;
        const residentId = req.residentId || req.resident?.id;
        if (!serviceId || !residentId) return;

        const key = `${serviceId}-${residentId}-${bucket}`;
        const prev = byServiceResident.get(key) || { count: 0, sum: 0 };

        const reqCost = this.getRequestCost(req);

        byServiceResident.set(key, {
          count: prev.count + 1,
          sum: prev.sum + reqCost,
          serviceId, residentId, bucket
        });
      });

      const rows = [];
      byServiceResident.forEach(({ count, sum, serviceId, residentId, bucket }) => {
        const serviceMeta = serviceMetaById.get(serviceId) || { serviceItemType: '', name: '' };
        const resident = this.residents.find(r => r.id === residentId);
        rows.push({
          serviceId, residentId,
          residentName: resident ? resident.name : '',
          bookedCount: count,
          spentMoney:  formatCurrency(+sum || 0, this.facilityCurrency),
          spentAverage: formatCurrency(count ? (sum / count) : 0, this.facilityCurrency),
          ...serviceMeta,
          serviceItemType: bucket,
        });
      });

      return rows.filter(r => r.bookedCount > 0).sort((a, b) => b.bookedCount - a.bookedCount);
    },

    handleChangePeriod(period) {
      this.date.start = moment().subtract(1, period).format("YYYY-MM-DD");
      this.date.end = moment().format("YYYY-MM-DD");
      this.loadReportData();
    }
  }
};
